import { useCallback, useEffect, useRef, useState } from "react";
import { RoutePanel } from "@/components/RoutePanel";
import { InstallAppButton } from "@/components/InstallAppButton";
import { SocietyHeader, BulkDeliverBar } from "@/components/SocietyDelivery";
import { Link } from "react-router-dom";
import { Camera, MapPin, Phone, CheckCircle2, XCircle, LogOut, MessageCircle, Loader2, ArrowLeft, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { Money, StatusPill, NativeSelect } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { todayIST, fmtDate, waLink, msgs } from "@/lib/format";

function DeliverSheet({ order, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [cash, setCash] = useState("");
  const [mode, setMode] = useState("cash");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef();
  useEffect(() => { setFile(null); setPreview(null); setCash(""); setReason(""); }, [order]);
  const pick = (e) => { const f = e.target.files?.[0]; if (f) { setFile(f); setPreview(URL.createObjectURL(f)); } };
  const submit = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("photo", file);
      fd.append("cash_collected", String(+cash || 0));
      fd.append("payment_mode", mode);
      await api.post(`/deliveries/${order.id}/deliver`, fd);
      toast.success("Delivered with photo proof");
      onDone({ ...order, delivery_status: "delivered" });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const fail = async () => {
    try { await api.post(`/deliveries/${order.id}/fail`, { reason: reason || "Customer not available" }); toast("Marked as not delivered"); onDone(null); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Drawer open={!!order} onOpenChange={(o) => !o && onClose()}>
      <DrawerContent className="max-h-[92vh]" data-testid="deliver-sheet">
        <div className="overflow-y-auto px-4 pb-6">
          <DrawerHeader className="px-0"><DrawerTitle className="font-display text-xl">{order?.customer_name}</DrawerTitle></DrawerHeader>
          <input ref={inputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid="photo-input" />
          <button type="button" onClick={() => inputRef.current?.click()} className="w-full rounded-2xl border-2 border-dashed border-emerald-600/40 bg-emerald-50/40 p-4 text-center hover:border-emerald-600 transition-colors" data-testid="photo-capture-btn">
            {preview ? <img src={preview} alt="Proof" className="mx-auto max-h-56 rounded-xl object-contain" /> : (
              <div className="py-6"><Camera className="mx-auto h-10 w-10 text-emerald-700" /><div className="mt-2 font-semibold text-emerald-800">Take delivery photo</div><div className="text-xs text-muted-foreground">Required as proof of delivery</div></div>
            )}
          </button>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Collected now (₹)</Label><Input type="number" inputMode="decimal" value={cash} onChange={(e) => setCash(e.target.value)} placeholder="0" className="h-12" data-testid="cash-input" /></div>
            <div className="space-y-1"><Label>Mode</Label><NativeSelect className="h-12" value={mode} onChange={(e) => setMode(e.target.value)} data-testid="cash-mode-select"><option value="cash">Cash</option><option value="upi">UPI</option></NativeSelect></div>
          </div>
          <div className="mt-2 text-xs text-muted-foreground">Order <Money value={order?.total} /> · Previous due <Money value={order?.balance} /></div>
          <Button disabled={!file || busy} onClick={submit} className="mt-5 h-14 w-full rounded-2xl bg-[#0F382C] hover:bg-[#134E3A] text-base" data-testid="confirm-delivery-btn">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><CheckCircle2 className="mr-2 h-5 w-5" />Confirm delivered</>}
          </Button>
          <div className="mt-6 border-t pt-4">
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason if not delivered (door locked...)" className="h-12" data-testid="fail-reason-input" />
            <Button variant="outline" onClick={fail} className="mt-2 h-12 w-full rounded-2xl border-rose-200 text-rose-700 hover:bg-rose-50" data-testid="mark-failed-btn"><XCircle className="mr-2 h-4 w-4" />Not delivered</Button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

function LoadList({ orders }) {
  const totals = {};
  orders.forEach((o) => o.items.forEach((i) => { totals[i.name] = (totals[i.name] || 0) + i.qty; }));
  const rows = Object.entries(totals);
  if (!rows.length) return null;
  return (
    <div className="mt-4 rounded-2xl border border-sky-200 bg-sky-50 p-4 text-sm" data-testid="tomorrow-load-list">
      <div className="font-display font-semibold text-sky-900">Tomorrow's load · {orders.length} confirmed orders</div>
      <div className="mt-2 space-y-1">{rows.map(([n, q]) => <div key={n} className="flex justify-between"><span>{n}</span><span className="font-mono font-semibold">× {q}</span></div>)}</div>
      <div className="mt-2 text-xs text-sky-800">View only. You can mark deliveries on the delivery day.</div>
    </div>
  );
}

export default function DeliveryApp() {
  const { user, logout } = useAuth();
  const [day, setDay] = useState(0);
  const date = todayIST(day);
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [mine, setMine] = useState(user.role === "delivery");
  const [active, setActive] = useState(null);
  const [bulk, setBulk] = useState(false);
  const [picked, setPicked] = useState([]);
  const [biz, setBiz] = useState("");
  const [justDone, setJustDone] = useState(null);
  const load = useCallback(() => api.get("/deliveries", { params: { date } }).then((r) => setOrders(r.data.orders)).catch((e) => toast.error(errMsg(e))), [date]);
  useEffect(() => { load(); api.get("/settings").then((r) => setBiz(r.data.business_name)).catch(() => {}); }, [load]);
  const scoped = orders.filter((o) => !mine || !o.delivery_boy_id || o.delivery_boy_id === user.id);
  const list = scoped.filter((o) => filter === "all" || (filter === "pending" ? o.delivery_status === "pending" : o.delivery_status !== "pending"));
  const done = scoped.filter((o) => o.delivery_status === "delivered").length;

  return (
    <div className="min-h-screen bg-[#FBF9F4] pb-10" data-testid="delivery-app">
      <header className="sticky top-0 z-20 bg-[#0F382C] px-4 pt-5 pb-4 text-white">
        <div className="mx-auto max-w-md">
          <div className="flex items-center justify-between">
            <div>
              {user.role === "admin" && <Link to="/deliveries" className="text-xs text-emerald-200 inline-flex items-center"><ArrowLeft className="mr-1 h-3 w-3" />Admin</Link>}
              <div className="font-display text-xl font-bold">Hi, {user.name.split(" ")[0]}</div>
              <div className="text-xs text-emerald-200">{fmtDate(date)} · {done}/{scoped.length} delivered</div>
            </div>
            <div className="flex items-center gap-1">
              <InstallAppButton variant="ghost" label="Install" className="bg-white/10 text-white hover:bg-white/20 hover:text-white" testId="delivery-install-app-btn" />
              <a href="/guides/delivery.pdf" target="_blank" rel="noreferrer" className="flex h-9 w-9 items-center justify-center rounded-md text-white hover:bg-white/10" aria-label="Help" data-testid="delivery-help-btn"><HelpCircle className="h-5 w-5" /></a>
              <Button variant="ghost" size="icon" className="text-white hover:bg-white/10" onClick={logout} data-testid="delivery-logout-btn"><LogOut className="h-5 w-5" /></Button>
            </div>
          </div>
          <Progress value={scoped.length ? (done / scoped.length) * 100 : 0} className="mt-3 h-2 bg-white/20" />
          <div className="mt-3 grid grid-cols-2 gap-1 rounded-full bg-white/10 p-1" data-testid="day-switch">
            {[[0, "Today"], [1, "Tomorrow"]].map(([d, l]) => (
              <button key={d} onClick={() => { setDay(d); setFilter("pending"); }} className={`rounded-full py-1.5 text-sm font-semibold transition-colors ${day === d ? "bg-[#FBF9F4] text-[#0F382C]" : "text-emerald-100"}`} data-testid={`day-${l.toLowerCase()}`}>{l}</button>
            ))}
          </div>
        </div>
      </header>
      <div className="mx-auto max-w-md px-4">
        <RoutePanel key={date} date={date} mine={mine} defaultOpen={false} />
        {day === 1 && <LoadList orders={scoped} />}
        <div className="mt-4 flex gap-2">
          {["pending", "done", "all"].map((f) => (
            <button key={f} onClick={() => setFilter(f)} className={`flex-1 rounded-full py-2 text-sm font-semibold capitalize transition-colors ${filter === f ? "bg-[#0F382C] text-white" : "bg-white border border-[#E7E2D6]"}`} data-testid={`filter-${f}`}>{f}</button>
          ))}
        </div>
        <label className="mt-3 flex items-center gap-2 text-sm text-muted-foreground"><input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} data-testid="mine-toggle" />Only my stops + unassigned</label>
        {justDone && (
          <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-4" data-testid="notify-customer-card">
            <div className="text-sm font-semibold text-emerald-800">Delivered to {justDone.customer_name}</div>
            <Button className="mt-2 w-full bg-[#25D366] hover:bg-[#20bd5a] text-white" onClick={() => { window.open(waLink(justDone.customer_phone, msgs.delivery({ name: justDone.customer_name, token: justDone.customer_token }, biz)), "_blank"); setJustDone(null); }} data-testid="notify-customer-btn">
              <MessageCircle className="mr-2 h-4 w-4" />Ask customer to confirm on WhatsApp
            </Button>
          </div>
        )}
        <div className="mt-4 space-y-3">
          {list.length === 0 && <div className="rounded-2xl border border-dashed p-8 text-center text-sm text-muted-foreground" data-testid="no-stops">No stops here.</div>}
          {day === 0 && list.some((o) => o.delivery_status === "pending") && (
            <button onClick={() => { setBulk(!bulk); setPicked([]); }} className="w-full rounded-full border border-[#0F382C]/30 py-2 text-sm font-semibold text-[#0F382C]" data-testid="bulk-mode-btn">{bulk ? "Exit bulk deliver" : "Bulk deliver (same tower, one photo)"}</button>
          )}
          {list.map((o, i) => (<div key={o.id}>
            <SocietyHeader o={o} prev={list[i - 1]} count={list.filter((x) => x.society_name === o.society_name && x.tower === o.tower).length} />
            <div className="mt-2 rounded-2xl border border-[#E7E2D6] bg-white p-4 shadow-sm space-y-3 bf-rise" data-testid={`stop-card-${o.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex gap-3">
                  {bulk && o.delivery_status === "pending" ? <input type="checkbox" className="mt-1 h-6 w-6 accent-[#0F382C]" checked={picked.includes(o.id)} onChange={() => setPicked(picked.includes(o.id) ? picked.filter((x) => x !== o.id) : [...picked, o.id])} data-testid={`bulk-pick-${o.id}`} />
                    : <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#FAF4E8] font-mono text-sm font-semibold">{i + 1}</div>}
                  <div><div className="font-semibold">{o.flat_no ? `${o.flat_no} · ` : ""}{o.customer_name}</div><div className="text-xs text-muted-foreground">{o.address || o.area}</div>{o.slot && <span className="mt-1 inline-block rounded-full bg-[#FAF4E8] px-2 py-0.5 text-[11px] font-semibold text-[#0F382C]" data-testid={`stop-slot-${o.id}`}>{o.slot}</span>}</div>
                </div>
                <StatusPill status={o.delivery_status} />
              </div>
              <div className="rounded-xl bg-[#FAF4E8] p-3 text-sm">
                {o.items.map((it) => <div key={it.product_id} className="flex justify-between"><span>{it.name}</span><span className="font-mono font-semibold">× {it.qty}</span></div>)}
                <div className="mt-2 flex justify-between border-t border-[#E7E2D6] pt-2 text-xs"><span>Bill <Money value={o.total} /></span><span className={o.balance > 0 ? "text-amber-700 font-semibold" : ""}>Total due <Money value={o.balance + (o.delivery_status === "delivered" ? 0 : o.total)} /></span></div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="flex-1" asChild><a href={`tel:${o.customer_phone}`} data-testid={`call-${o.id}`}><Phone className="mr-1 h-4 w-4" />Call</a></Button>
                <Button variant="outline" size="sm" className="flex-1" asChild><a href={o.maps_url || (o.lat != null ? `https://www.google.com/maps/search/?api=1&query=${o.lat},${o.lng}` : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(o.address + " " + o.area)}`)} target="_blank" rel="noreferrer" data-testid={`map-${o.id}`}><MapPin className="mr-1 h-4 w-4" />{o.lat != null ? "Map" : "Map (no pin)"}</a></Button>
              </div>
              {o.delivery_status !== "delivered" && day === 0 && (
                <Button onClick={() => setActive(o)} className="h-12 w-full rounded-xl bg-[#0F382C] hover:bg-[#134E3A]" data-testid={`deliver-btn-${o.id}`}><Camera className="mr-2 h-4 w-4" />Deliver with photo</Button>
              )}
            </div>
          </div>))}
        </div>
      </div>
      {bulk && <BulkDeliverBar ids={picked} onCancel={() => { setBulk(false); setPicked([]); }} onDone={() => { setBulk(false); setPicked([]); load(); }} />}
      <DeliverSheet order={active} onClose={() => setActive(null)} onDone={(o) => { setActive(null); setJustDone(o); load(); }} />
    </div>
  );
}
